'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Music, Play, Pause, SkipForward, Volume2, Settings, RefreshCw, AlertCircle, Sparkles, Disc, Radio, Folder, File, ChevronRight, Home, ArrowLeft } from 'lucide-react';

interface DirectoryItem {
  id: string;
  name: string;
  isDir: boolean;
  artist?: string;
  duration?: number;
  coverArt?: string;
}

interface Breadcrumb {
  id: string;
  name: string;
}

export default function MusicControl() {
  // Gonic Settings
  const [gonicUrl, setGonicUrl] = useState('http://192.168.1.50:4747');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isConfigured, setIsConfigured] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [configError, setConfigError] = useState('');

  // Folder Browsing State
  const [items, setItems] = useState<DirectoryItem[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string>('root');
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([{ id: 'root', name: 'Musik' }]);
  const [isLoadingDirectory, setIsLoadingDirectory] = useState(false);

  // Player State (Currently playing item)
  const [playingSong, setPlayingSong] = useState<DirectoryItem | null>(null);
  const [playingQueue, setPlayingQueue] = useState<DirectoryItem[]>([]); 
  const [currentQueueIndex, setCurrentQueueIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.5);

  // Toggle settings panel
  const [showSettings, setShowSettings] = useState(false);

  // Audio element reference (Persists across the entire lifespan)
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Keep references to prevent stale closures in event listeners
  const queueRef = useRef<DirectoryItem[]>([]);
  const indexRef = useRef<number>(-1);

  useEffect(() => {
    queueRef.current = playingQueue;
  }, [playingQueue]);

  useEffect(() => {
    indexRef.current = currentQueueIndex;
  }, [currentQueueIndex]);

  // Load configuration on mount
  useEffect(() => {
    const savedUrl = localStorage.getItem('lan_gonic_url');
    const savedUser = localStorage.getItem('lan_gonic_user');
    const savedPass = localStorage.getItem('lan_gonic_pass');

    if (savedUrl) setGonicUrl(savedUrl);
    if (savedUser) setUsername(savedUser);
    if (savedPass) setPassword(savedPass);

    if (savedUser && savedPass) {
      setIsConfigured(true);
    } else {
      setShowSettings(true);
    }
  }, []);

  // Initialize and load directory once configured
  useEffect(() => {
    if (isConfigured && username && password) {
      loadRootDirectory();
    }
  }, [isConfigured]);

  // Audio event listeners - BOUND EXACTLY ONCE ON MOUNT
  useEffect(() => {
    if (typeof window === 'undefined') return;

    console.log("=== Initialisiere HTML5 Audio Element ===");
    const audio = new Audio();
    audioRef.current = audio;
    audio.volume = volume;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration || 0);
    const onEnded = () => {
      console.log("Audio beendet, spiele nächstes Lied...");
      playNext();
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);

    return () => {
      console.log("=== Räume HTML5 Audio Element auf ===");
      audio.pause();
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
      audioRef.current = null;
    };
  }, []); // EMPTY dependency array! Persists forever!

  // Load root directory using getIndexes
  const loadRootDirectory = async () => {
    if (!username || !password) return;
    setIsLoadingDirectory(true);
    console.log("=== Lade Gonic-Hauptverzeichnis ===");
    try {
      const res = await fetch(`/api/gonic?action=getIndexes&url=${encodeURIComponent(gonicUrl)}&u=${encodeURIComponent(username)}&p=${encodeURIComponent(password)}`);
      if (!res.ok) throw new Error('Hauptverzeichnis-Anfrage fehlgeschlagen');
      const data = await res.json();
      console.log("getIndexes Antwort für Hauptverzeichnis:", data);
      
      const responseNode = data['subsonic-response'];
      if (responseNode?.status === 'failed') {
        const errorMsg = responseNode?.error?.message || 'Gonic Authentifizierung fehlgeschlagen.';
        throw new Error(errorMsg);
      }

      const indexesNode = responseNode?.indexes;
      let rawChildren = indexesNode?.child || [];
      
      let rawIndex = indexesNode?.index || [];
      if (rawIndex && !Array.isArray(rawIndex)) {
        rawIndex = [rawIndex];
      }
      
      if (rawChildren && !Array.isArray(rawChildren)) {
        rawChildren = [rawChildren];
      }

      for (const idx of rawIndex) {
        let artists = idx.artist || [];
        if (artists && !Array.isArray(artists)) {
          artists = [artists];
        }
        for (const art of artists) {
          rawChildren.push({
            id: art.id,
            name: art.name,
            isDir: true
          });
        }
      }

      const formatted: DirectoryItem[] = rawChildren.map((c: any) => ({
        id: c.id,
        name: c.title || c.name || 'Unbekannt',
        isDir: c.isDir === true || c.isDir === 'true',
        artist: c.artist,
        duration: c.duration,
        coverArt: c.coverArt
      }));

      // Whitelist Filter
      const allowedRootFolders = [
        "deutsch", 
        "englisch", 
        "edm", 
        "mixes", 
        "stimmung & party", 
        "stimmung", 
        "party"
      ];
      
      const filtered = formatted.filter(item => {
        if (item.isDir) {
          const nameLower = item.name.toLowerCase().trim();
          return allowedRootFolders.some(allowed => nameLower.includes(allowed));
        }
        return true;
      });

      filtered.sort((a, b) => {
        if (a.isDir && !b.isDir) return -1;
        if (!a.isDir && b.isDir) return 1;
        return a.name.localeCompare(b.name);
      });

      setBreadcrumbs([{ id: 'root', name: 'Musik' }]);
      setCurrentFolderId('root');
      setItems(filtered);
      
      const playableFiles = filtered.filter(item => !item.isDir);
      setPlayingQueue(playableFiles);

    } catch (e: any) {
      console.error("Fehler beim Laden des Hauptverzeichnisses:", e);
      setConfigError(e.message || 'Konnte das Musik-Hauptverzeichnis von Gonic nicht laden.');
      setShowSettings(true);
      setIsConfigured(false);
    } finally {
      setIsLoadingDirectory(false);
    }
  };

  // Load specific folder contents
  const loadFolderContents = async (folderId: string) => {
    if (!username || !password) return;
    
    if (folderId === 'root') {
      await loadRootDirectory();
      return;
    }

    setIsLoadingDirectory(true);
    setCurrentFolderId(folderId);
    console.log(`=== Lade Ordner-Inhalt für ID: ${folderId} ===`);
    try {
      const res = await fetch(`/api/gonic?action=getMusicDirectory&url=${encodeURIComponent(gonicUrl)}&u=${encodeURIComponent(username)}&p=${encodeURIComponent(password)}&id=${folderId}`);
      if (!res.ok) throw new Error('Ordnerinhalt-Anfrage fehlgeschlagen');
      
      const data = await res.json();
      console.log("getMusicDirectory Antwort:", data);
      
      const responseNode = data['subsonic-response'];
      if (responseNode?.status === 'failed') {
        const errorMsg = responseNode?.error?.message || 'Gonic Authentifizierung fehlgeschlagen.';
        throw new Error(errorMsg);
      }

      let rawChildren = responseNode?.directory?.child || [];
      if (rawChildren && !Array.isArray(rawChildren)) {
        rawChildren = [rawChildren];
      }
      
      const formatted: DirectoryItem[] = rawChildren.map((c: any) => ({
        id: c.id,
        name: c.title || c.name || 'Unbekannt',
        isDir: c.isDir === true || c.isDir === 'true',
        artist: c.artist,
        duration: c.duration,
        coverArt: c.coverArt
      }));

      formatted.sort((a, b) => {
        if (a.isDir && !b.isDir) return -1;
        if (!a.isDir && b.isDir) return 1;
        return a.name.localeCompare(b.name);
      });

      setItems(formatted);
      
      const playableFiles = formatted.filter(item => !item.isDir);
      setPlayingQueue(playableFiles);

    } catch (e: any) {
      console.error(`Kritischer Fehler beim Laden des Ordners ${folderId}:`, e);
      setConfigError(e.message || 'Konnte den Ordnerinhalt nicht laden.');
      setShowSettings(true);
      setIsConfigured(false);
    } finally {
      setIsLoadingDirectory(false);
    }
  };

  // Handle item click
  const handleItemClick = async (item: DirectoryItem) => {
    if (item.isDir) {
      const newBreadcrumbs = [...breadcrumbs, { id: item.id, name: item.name }];
      setBreadcrumbs(newBreadcrumbs);
      await loadFolderContents(item.id);
    } else {
      const indexInQueue = playingQueue.findIndex(q => q.id === item.id);
      if (indexInQueue !== -1) {
        playQueueItem(indexInQueue);
      }
    }
  };

  // Play from queue - PERFECT EXCLUSION OF RE-CREATION
  const playQueueItem = (index: number) => {
    const queue = queueRef.current;
    const song = queue[index];
    if (!audioRef.current || !song) return;

    console.log(`=== Spiele Song: ${song.name} (Index: ${index}) ===`);
    setPlayingSong(song);
    setCurrentQueueIndex(index);

    const streamUrl = `/api/gonic?action=stream&url=${encodeURIComponent(gonicUrl)}&u=${encodeURIComponent(username)}&p=${encodeURIComponent(password)}&id=${song.id}`;
    
    // We modify the existing element!
    audioRef.current.src = streamUrl;
    audioRef.current.load();
    audioRef.current.play()
      .then(() => {
        setIsPlaying(true);
        console.log("Playback gestartet!");
      })
      .catch(e => {
        console.warn("Wiedergabe-Versuch blockiert oder abgebrochen:", e);
      });
  };

  const togglePlay = () => {
    if (!audioRef.current || !playingSong) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play()
        .then(() => setIsPlaying(true))
        .catch(e => console.error(e));
    }
  };

  const playNext = () => {
    const queue = queueRef.current;
    const index = indexRef.current;
    if (queue.length === 0) return;
    const nextIndex = (index + 1) % queue.length;
    playQueueItem(nextIndex);
  };

  const playPrev = () => {
    const queue = queueRef.current;
    const index = indexRef.current;
    if (queue.length === 0) return;
    const prevIndex = (index - 1 + queue.length) % queue.length;
    playQueueItem(prevIndex);
  };

  const navigateToBreadcrumb = async (crumb: Breadcrumb, idx: number) => {
    const newCrumbs = breadcrumbs.slice(0, idx + 1);
    setBreadcrumbs(newCrumbs);
    await loadFolderContents(crumb.id);
  };

  const navigateUp = async () => {
    if (breadcrumbs.length <= 1) return;
    const parentIndex = breadcrumbs.length - 2;
    await navigateToBreadcrumb(breadcrumbs[parentIndex], parentIndex);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (audioRef.current) {
      audioRef.current.volume = val;
    }
  };

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setCurrentTime(val);
    if (audioRef.current) {
      audioRef.current.currentTime = val;
    }
  };

  // Test and save Gonic settings
  const testAndSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsTesting(true);
    setConfigError('');

    try {
      const res = await fetch(`/api/gonic?action=ping&url=${encodeURIComponent(gonicUrl)}&u=${encodeURIComponent(username)}&p=${encodeURIComponent(password)}`);
      const result = await res.json();

      if (res.ok && result.success) {
        localStorage.setItem('lan_gonic_url', gonicUrl);
        localStorage.setItem('lan_gonic_user', username);
        localStorage.setItem('lan_gonic_pass', password);
        
        setIsConfigured(true);
        setShowSettings(false);
      } else {
        throw new Error(result.error || 'Server antwortet nicht mit OK. Passwort oder Name falsch?');
      }
    } catch (err: any) {
      setConfigError(err.message || 'Verbindung fehlgeschlagen. Bitte IP und Port prüfen.');
    } finally {
      setIsTesting(false);
    }
  };

  // Helper formatting mm:ss
  const formatTime = (secs: number) => {
    if (isNaN(secs)) return '0:00';
    const mins = Math.floor(secs / 60);
    const remaining = Math.floor(secs % 60);
    return `${mins}:${remaining < 10 ? '0' : ''}${remaining}`;
  };

  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden shadow-2xl h-full flex flex-col relative group">
      
      {/* HEADER */}
      <div className="p-4 bg-slate-900 border-b border-slate-800 flex justify-between items-center z-10 relative">
        <div className="flex items-center gap-3">
          <Music className="text-pink-500 w-6 h-6 animate-pulse" />
          <div>
            <h2 className="text-xl font-bold text-white uppercase tracking-wider leading-none mb-1">DJ Station (Gonic Explorer)</h2>
            <p className="text-[10px] text-pink-500/80 font-bold uppercase tracking-widest">Ordnerbasierter Musik-Sichter</p>
          </div>
        </div>
        <button 
          onClick={() => setShowSettings(!showSettings)}
          className="bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-bold p-2 rounded transition-colors flex items-center gap-1.5"
        >
          <Settings className="w-4 h-4" /> Config
        </button>
      </div>

      <div className="flex-1 bg-slate-950 p-4 flex flex-col justify-between relative overflow-hidden min-h-[500px]">
        
        {/* SETTINGS PANEL OVERLAY */}
        {showSettings && (
          <div className="absolute inset-0 bg-slate-950/95 z-50 p-6 flex flex-col justify-center overflow-y-auto">
            <h3 className="text-lg font-bold text-white uppercase mb-4 flex items-center gap-2">
              <Settings className="text-pink-500 w-5 h-5" /> Gonic Zugangsdaten
            </h3>
            <form onSubmit={testAndSaveSettings} className="space-y-4 text-sm">
              <div>
                <label className="block text-slate-400 font-bold mb-1 uppercase text-xs">Gonic URL</label>
                <input 
                  type="text" 
                  value={gonicUrl}
                  onChange={(e) => setGonicUrl(e.target.value)}
                  placeholder="http://192.168.1.50:4747"
                  className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-white focus:outline-none focus:border-pink-500"
                />
              </div>
              <div>
                <label className="block text-slate-400 font-bold mb-1 uppercase text-xs">Benutzername</label>
                <input 
                  type="text" 
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="z.B. patrick"
                  required
                  className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-white focus:outline-none focus:border-pink-500"
                />
              </div>
              <div>
                <label className="block text-slate-400 font-bold mb-1 uppercase text-xs">Passwort</label>
                <input 
                  type="password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Gonic Passwort"
                  required
                  className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-white focus:outline-none focus:border-pink-500"
                />
              </div>

              {configError && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded flex items-start gap-2 text-xs">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <p>{configError}</p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button 
                  type="submit"
                  disabled={isTesting}
                  className="flex-1 bg-pink-600 hover:bg-pink-500 text-white font-bold py-2 rounded transition-all flex items-center justify-center gap-2"
                >
                  {isTesting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  VERBINDEN & SPEICHERN
                </button>
                {isConfigured && (
                  <button 
                    type="button"
                    onClick={() => setShowSettings(false)}
                    className="bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded"
                  >
                    Abbrechen
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {/* BREADCRUMBS & NAVIGATION */}
        {isConfigured && (
          <div className="flex items-center gap-2 mb-3 bg-slate-900/60 p-2 border border-slate-800 rounded-lg text-xs">
            <button 
              onClick={navigateUp}
              disabled={breadcrumbs.length <= 1}
              className="p-1 hover:bg-slate-800 text-slate-400 hover:text-white rounded disabled:opacity-30 disabled:hover:bg-transparent"
              title="Ordner nach oben"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="h-4 w-[1px] bg-slate-800 mx-1"></div>
            
            <div className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar flex-1 whitespace-nowrap text-slate-300 font-bold">
              <Home className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              {breadcrumbs.map((crumb, idx) => (
                <React.Fragment key={crumb.id}>
                  {idx > 0 && <ChevronRight className="w-3 h-3 text-slate-600 shrink-0" />}
                  <button 
                    onClick={() => navigateToBreadcrumb(crumb, idx)}
                    className={`hover:text-pink-500 truncate ${idx === breadcrumbs.length - 1 ? 'text-pink-500 font-black' : ''}`}
                  >
                    {crumb.name}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* FILE EXPLORER BROWSER LIST */}
        <div className="flex-1 min-h-[220px] overflow-y-auto border border-slate-800 rounded-lg bg-slate-950 p-2 mb-4 custom-scrollbar">
          {isLoadingDirectory ? (
            <div className="h-full flex items-center justify-center text-slate-500">
              <RefreshCw className="w-8 h-8 animate-spin text-pink-500" />
            </div>
          ) : items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-500 space-y-3 opacity-60">
              <Folder className="w-12 h-12 text-slate-700 animate-pulse" />
              <div className="text-center p-4">
                <p className="text-xs font-bold text-white mb-1">Keine Elemente gefunden</p>
                <p className="text-[10px] text-slate-600">Öffnet die Entwicklerkonsole (F12) Eures Browsers, um eventuelle Fehler der Gonic API zu ergründen.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              {items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className={`w-full text-left px-3 py-2 rounded flex items-center justify-between text-xs transition-all ${playingSong?.id === item.id ? 'bg-pink-600/10 text-pink-500 border border-pink-500/10 font-bold' : 'hover:bg-slate-900 text-slate-400 hover:text-white'}`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {item.isDir ? (
                      <Folder className="w-4 h-4 text-amber-500 shrink-0" />
                    ) : (
                      <File className={`w-4 h-4 shrink-0 ${playingSong?.id === item.id ? 'text-pink-500' : 'text-slate-500'}`} />
                    )}
                    <span className="truncate flex-1">{item.name}</span>
                  </div>
                  {!item.isDir && (
                    <div className="flex items-center gap-4 text-[10px] text-slate-600 font-bold ml-2 shrink-0">
                      {item.artist && <span className="truncate max-w-[120px] text-slate-500">{item.artist}</span>}
                      {item.duration && <span>{formatTime(item.duration)}</span>}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* MINI MEDIA PLAYER SECTION (Active song playing) */}
        {playingSong && (
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-3 flex flex-col space-y-3 shadow-lg">
            
            {/* Metadata & Controls */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className={`w-10 h-10 rounded-full bg-slate-950 border border-slate-800 flex items-center justify-center shrink-0 overflow-hidden relative ${isPlaying ? 'animate-spin [animation-duration:15s]' : ''}`}>
                  {playingSong.coverArt ? (
                    <img 
                      src={`/api/gonic?action=getCoverArt&url=${encodeURIComponent(gonicUrl)}&u=${encodeURIComponent(username)}&p=${encodeURIComponent(password)}&id=${playingSong.id}`}
                      alt="Cover Art"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Disc className="w-5 h-5 text-pink-500/50" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-bold text-white truncate">{playingSong.name}</h4>
                  <p className="text-slate-400 text-[10px] font-medium truncate">{playingSong.artist || 'Gonic Stream'}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-1">
                <button 
                  onClick={playPrev}
                  className="p-1.5 text-slate-400 hover:text-white rounded-full transition-colors"
                >
                  <SkipForward className="w-4 h-4 rotate-180" />
                </button>
                <button 
                  onClick={togglePlay}
                  className="bg-pink-600 hover:bg-pink-500 text-white rounded-full p-2 active:scale-95 transition-all shadow-md"
                >
                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 fill-white ml-0.5" />}
                </button>
                <button 
                  onClick={playNext}
                  className="p-1.5 text-slate-400 hover:text-white rounded-full transition-colors"
                >
                  <SkipForward className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Progress Slider */}
            <div className="space-y-1">
              <div className="flex justify-between text-[10px] font-bold text-slate-500 leading-none">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
              <div className="flex items-center gap-3">
                <input 
                  type="range"
                  min="0"
                  max={duration}
                  value={currentTime}
                  onChange={handleProgressChange}
                  className="flex-1 h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-pink-500 focus:outline-none"
                />
                <div className="flex items-center gap-1 shrink-0">
                  <Volume2 className="text-slate-500 w-3.5 h-3.5" />
                  <input 
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={volume}
                    onChange={handleVolumeChange}
                    className="w-12 h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-pink-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>

          </div>
        )}

      </div>

    </div>
  );
}
